import { lazy } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { CircularProgress, Stack, Typography } from '@mui/material';

// project imports
import Loadable from 'components/Loadable';
import ProtectedRoute from 'components/ProtectedRoute';
import DashboardLayout from 'layout/Dashboard';
import { useAuth } from 'contexts/AuthContext';
import { hasAreaSubareaAccess, useAllowedAreas } from 'hooks/usePermissions.jsx';

const AreaSelectorGestor = Loadable(lazy(() => import('pages/area-selector-gestor')));
const AreaSelectorVrp = Loadable(lazy(() => import('pages/area-selector-vrp')));
const AreaSelectorGestion = Loadable(lazy(() => import('pages/area-selector-gestion')));
const AreaSelectorPlanEstrategico = Loadable(lazy(() => import('pages/area-selector-plan-estrategico')));
const PortalUsuarioLanding = Loadable(lazy(() => import('pages/portal-usuario')));
const PortalUsuarioUserAccess = Loadable(lazy(() => import('pages/portal-usuario/user-access')));
const PortalUsuarioFullAccess = Loadable(lazy(() => import('pages/portal-usuario/full-access')));
const PlanEstrategicoDocumentacion = Loadable(lazy(() => import('pages_plan_estrategico/documentacion-plan-estrategico')));
const PlanEstrategicoImagenCorporativa = Loadable(lazy(() => import('pages_plan_estrategico/imagen-corporativa')));
const PlanEstrategicoCiberseguridad = Loadable(lazy(() => import('pages_plan_estrategico/ciberseguridad')));
const PlanEstrategicoSostenibilidad = Loadable(lazy(() => import('pages_plan_estrategico/sostenibilidad')));
const PlanEstrategicoGestionResiduos = Loadable(lazy(() => import('pages_plan_estrategico/gestion-residuos')));
const StandaloneUserManagement = Loadable(lazy(() => import('pages/users/StandaloneUserManagement')));
const StandalonePhoneDirectory = Loadable(lazy(() => import('pages/telefonos/StandalonePhoneDirectory')));
const AgendaCalendario = Loadable(lazy(() => import('pages_agenda/calendario')));
const MantenimientosCalendario = Loadable(lazy(() => import('pages_mantenimientos_periodicos/calendario')));
const InstrumentacionCalendario = Loadable(lazy(() => import('pages_instrumentacion/calendario')));
const VrpReports = Loadable(lazy(() => import('pages/informes/VrpReports')));
const DesviacionHorasReport = Loadable(lazy(() => import('pages/informes/DesviacionHoras')));
const InformesPresupuestos = Loadable(lazy(() => import('pages/informes/InformesPresupuestos')));
const ResumenObras = Loadable(lazy(() => import('pages/informes/ResumenObras')));
const IsoDashboardDefault = Loadable(lazy(() => import('pages_ISO/dashboard/default')));
const IsoDocumentacion = Loadable(lazy(() => import('pages_ISO/documentacion')));
const GestionProyectos = Loadable(lazy(() => import('pages/presupuestos/GestionProyectos')));
const PresupuestosEntryRedirect = Loadable(lazy(() => import('pages/presupuestos/EntryRedirect')));
const INFORMES_AREA_SLUG = 'informes';
const RESUMEN_OBRAS_SUBAREA_SLUG = 'resumen-obras';

function ResumenObrasAccessGate() {
  const { user } = useAuth();
  const { allowedAreas, loading, error } = useAllowedAreas();
  const normalizedRole = String(user?.role || '').trim().toLowerCase();

  if (normalizedRole === 'admin') {
    return <ResumenObras />;
  }

  const hasAccess = hasAreaSubareaAccess(
    allowedAreas,
    INFORMES_AREA_SLUG,
    RESUMEN_OBRAS_SUBAREA_SLUG,
    normalizedRole
  );
  const hasInformesArea = Array.isArray(allowedAreas)
    && allowedAreas.some((area) => String(area?.slug || '').trim().toLowerCase() === INFORMES_AREA_SLUG);
  const fallbackPath = hasInformesArea ? '/areas/informes' : '/areas';

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '40vh' }} spacing={1.5}>
        <CircularProgress size={28} thickness={5} />
        <Typography variant="body2" color="text.secondary">
          Comprobando permisos...
        </Typography>
      </Stack>
    );
  }

  if (error || !hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <ResumenObras />;
}
// ==============================|| AREAS (SELECTOR) ROUTING ||============================== //

const AreasRoutes = {
  path: '/areas',
  element: (
    <ProtectedRoute>
      <Outlet />
    </ProtectedRoute>
  ),
  children: [
    {
      index: true,
      element: <AreaSelectorVrp />
    },
    {
      path: 'gestion',
      element: <Outlet />,
      children: [
        {
          index: true,
          element: <AreaSelectorGestion />
        },
        {
          path: 'usuarios',
          element: <StandaloneUserManagement />
        },
        {
          path: 'telefonos',
          element: <StandalonePhoneDirectory />
        }
      ]
    },
    {
      path: 'plan-estrategico',
      children: [
        {
          index: true,
          element: <AreaSelectorPlanEstrategico />
        },
        {
          path: ':subareaSlug',
          element: <AreaSelectorPlanEstrategico />
        },
        {
          element: <DashboardLayout />,
          children: [
            {
              path: 'documentacion-plan-estrategico',
              element: <PlanEstrategicoDocumentacion />
            },
            {
              path: 'imagen-corporativa',
              element: <PlanEstrategicoImagenCorporativa />
            },
            {
              path: 'ciberseguridad',
              element: <PlanEstrategicoCiberseguridad />
            },
            {
              path: 'sostenibilidad',
              element: <PlanEstrategicoSostenibilidad />
            },
            {
              path: 'gestion-residuos',
              element: <PlanEstrategicoGestionResiduos />
            }
          ]
        }
      ]
    },
    {
      path: 'portal-usuario',
      element: <Outlet />,
      children: [
        {
          index: true,
          element: <PortalUsuarioLanding />
        },
        {
          path: 'user_access',
          element: <DashboardLayout />,
          children: [
            {
              index: true,
              element: <Navigate to="pendientes" replace />
            },
            {
              path: ':sectionSlug',
              element: <PortalUsuarioUserAccess />
            }
          ]
        },
        {
          path: 'full_access',
          element: <DashboardLayout />,
          children: [
            {
              index: true,
              element: <Navigate to="carpetas" replace />
            },
            {
              path: ':sectionSlug',
              element: <PortalUsuarioFullAccess />
            }
          ]
        }
      ]
    },
    {
      path: 'gestor',
      element: <AreaSelectorGestor />
    },
    {
      path: 'agenda',
      element: <DashboardLayout />,
      children: [
        {
          index: true,
          element: <Navigate to="calendario" replace />
        },
        {
          path: 'calendario',
          element: <AgendaCalendario />
        }
      ]
    },
    {
      path: 'mantenimientos-periodicos',
      element: <DashboardLayout />,
      children: [
        {
          index: true,
          element: <Navigate to="calendario" replace />
        },
        {
          path: 'calendario',
          element: <MantenimientosCalendario />
        }
      ]
    },
    {
      path: 'instrumentacion',
      element: <DashboardLayout />,
      children: [
        {
          index: true,
          element: <Navigate to="calendario" replace />
        },
        {
          path: 'calendario',
          element: <InstrumentacionCalendario />
        }
      ]
    },
    {
      path: 'informes',
      element: <DashboardLayout />,
      children: [
        {
          index: true,
          element: <InformesPresupuestos />
        },
        {
          path: 'desviacion-horas',
          element: <DesviacionHorasReport />
        },
        {
          path: 'presupuestos',
          element: <InformesPresupuestos />
        },
        {
          path: 'resumen-obras',
          element: <ResumenObrasAccessGate />
        },
        {
          path: 'vrp-crm',
          element: <VrpReports />
        }
      ]
    },
    {
      path: 'presupuestos',
      element: <DashboardLayout />,
      children: [
        {
          index: true,
          element: <PresupuestosEntryRedirect />
        },
        {
          path: 'gestion-proyectos',
          element: <GestionProyectos />
        },
      ]
    },
    {
      path: 'iso',
      element: <DashboardLayout />,
      children: [
        {
          index: true,
          element: <IsoDocumentacion />
        },
        {
          path: 'documentacion',
          element: <IsoDocumentacion />
        },
        {
          path: 'dashboard',
          children: [
            {
              index: true,
              element: <Navigate to="default" replace />
            },
            {
              path: 'default',
              element: <IsoDashboardDefault />
            }
          ]
        }
      ]
    },
  ]
};

export default AreasRoutes;
